import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { addDays } from "date-fns"
import JobSuggestionModal from "./JobSuggestionModal"

// Days of the week for the schedule
const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

/**
 * SuggestionRow component displays a row with "Add" buttons for each working day
 * that allows operations managers to quickly assign suggested job services.
 *
 * @param {Object} props
 * @param {number} props.surveyorId - Surveyor ID
 * @param {string} props.surveyorName - Surveyor name for display
 * @param {Date} props.weekStartDate - Start date of the week (Monday)
 * @param {Object} props.workingSchedule - Surveyor's working schedule
 * @param {Object} props.scheduleData - Schedule data with job services and metrics
 * @param {Function} props.onAssignmentComplete - Callback when assignment completes
 * @param {boolean} props.isUpdating - Whether an update operation is in progress
 */
export default function SuggestionRow({
  surveyorId,
  surveyorName,
  weekStartDate,
  workingSchedule,
  scheduleData,
  onAssignmentComplete,
  isUpdating
}) {
  const [selectedDay, setSelectedDay] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Handle "Add" button click
  const handleAddClick = useCallback((dayIndex, date) => {
    setSelectedDay(date)
    setIsModalOpen(true)
  }, [])

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    setSelectedDay(null)
  }, [])

  // Handle assignment success
  const handleAssignmentSuccess = useCallback(() => {
    handleModalClose()
    if (onAssignmentComplete) {
      onAssignmentComplete()
    }
  }, [handleModalClose, onAssignmentComplete])

  // Render "Add" button for a day
  const renderAddButton = useCallback(dayIndex => {
    const dayDate = addDays(weekStartDate, dayIndex)
    const dayKey = WEEK_DAYS[dayIndex].toLowerCase()
    const daySchedule = workingSchedule[dayKey]
    const isWorking = daySchedule?.works

    if (!isWorking) {
      return (
        <span className="not-working-text">-</span>
      )
    }

    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-primary add-suggestion-btn"
        onClick={() => handleAddClick(dayIndex, dayDate)}
        disabled={isUpdating}
        title="Add suggested job service">
        <span className="add-icon">+</span>
      </button>
    )
  }, [weekStartDate, workingSchedule, isUpdating, handleAddClick])

  return (
    <>
      <tr className="suggestion-row">
        <td className="surveyor-name-col suggestion-label-cell">
        </td>

        {/* Day columns with Add buttons */}
        {WEEK_DAYS.map((day, dayIndex) => {
          const dayKey = WEEK_DAYS[dayIndex].toLowerCase()
          const daySchedule = workingSchedule[dayKey]
          const isWorking = daySchedule?.works
          
          return (
            <td key={day} className={`day-col suggestion-cell ${isWorking ? 'working-day' : 'non-working-day'}`}>
              <div>
                {renderAddButton(dayIndex)}
              </div>
            </td>
          )
        })}

        {/* Total column */}
        <td className="total-col">
          <div />
        </td>
      </tr>

      {/* Job Suggestion Modal */}
      {isModalOpen && selectedDay && (
        <JobSuggestionModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          surveyorId={surveyorId}
          surveyorName={surveyorName}
          selectedDate={selectedDay}
          workingSchedule={workingSchedule}
          scheduleData={scheduleData}
          onAssignmentSuccess={handleAssignmentSuccess} />
      )}
    </>
  )
}

SuggestionRow.propTypes = {
  surveyorId: PropTypes.number.isRequired,
  surveyorName: PropTypes.string.isRequired,
  weekStartDate: PropTypes.instanceOf(Date).isRequired,
  workingSchedule: PropTypes.objectOf(
    PropTypes.shape({
      works: PropTypes.bool,
      start_time: PropTypes.string,
      end_time: PropTypes.string
    })
  ).isRequired,
  scheduleData: PropTypes.shape({}),
  onAssignmentComplete: PropTypes.func,
  isUpdating: PropTypes.bool
}

SuggestionRow.defaultProps = {
  scheduleData: null,
  onAssignmentComplete: null,
  isUpdating: false
}
